"use client";
import React, { useState, useEffect } from "react";
// Drawer modal removed - using fixed positioning drawer instead
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import axios from "axios";
import { API_BASE_URL } from "../../../../src/App.js";
import { useSelector } from "react-redux";

import { formatQuotationMoney, parseQuotationMoney } from "./quotationPdfShared";

interface Product {
  id: string;
  name: string;
  gst: number;
  price: number;
  articleNo?: string;
  hsnCode?: string;
  unit?: string;
  moq?: number;
  isCloth?: boolean;
  specifications?: { title: string; type: string; value?: string[] }[];
  formulaType?: string;
  description?: string;
}

interface ProductItem {
  product_id: string;
  name: string;
  description?: string;
  width: number;
  wh_uom: string;
  nool_width: number;
  height: number;
  nool_height: number;
  nos: number;
  place: string;
  area: number;
  area_uom: string;
  area_per_unit: number;
  gst: number;
  price: number;
  customProductBasePrice?: number; // Base price for custom products (before multiplying by quantity)
  [key: string]: any;
}

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (products: ProductItem[]) => void;
  userRole: "admin" | "dealer" | "employee";
  customerRole:string;
  existingProduct?: ProductItem | null;
  otherModalOpen?: boolean;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  onAdd,
  userRole,
  customerRole,
   existingProduct,
   otherModalOpen = false,
}) => {
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [sections, setSections] = useState<
    { selectedProduct: Product | null; form: Partial<ProductItem> }[]
  >([
    {
      selectedProduct: null,
      form: {
        wh_uom: "m",
        length_uom: "m",
        area_uom: "ft",
        place: "room",
        gst: 0,
        price: 0,
        customProductBasePrice: undefined,
      },
    },
  ]);

  const { authToken = "", id = "",role="" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const [colors, setColors] = useState<any[]>([]);
  const [showColorDropdown, setShowColorDropdown] = useState<{ [key: number]: boolean }>({});
  const [sectionErrors, setSectionErrors] = useState<{ [key: number]: { [key: string]: string } }>({});
  const [openProductPicker, setOpenProductPicker] = useState<number | null>(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoadError, setProductsLoadError] = useState<string | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [manualPriceEdited, setManualPriceEdited] = useState<{ [key: number]: boolean }>({});
  const [previewColor, setPreviewColor] = useState<any>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);


  useEffect(() => {
  if (existingProduct) {
    // Find the full product data from productsList to get specifications (if available)
    // Don't wait for productsList to load - populate form immediately with existing product data
    const fullProduct = productsList.length > 0 
      ? productsList.find((p) => p.id === existingProduct.product_id)
      : null;
    
    // Prepare form data with specifications properly mapped
    const formData = { ...existingProduct };
    
    // If specifications exist in the existing product, map them to form fields
    if (existingProduct.specifications) {
      Object.keys(existingProduct.specifications).forEach((specTitle) => {
        const specValue = existingProduct.specifications[specTitle];
        // Handle both simple values and objects with name property
        const value = specValue?.name || specValue;
        formData[specTitle.toLowerCase()] = value;
      });
    }
    
    setSections([
      {
        selectedProduct: fullProduct || {
          id: existingProduct.product_id,
          name: existingProduct.name,
          gst: existingProduct.gst,
          price: existingProduct.price,
        },
        form: formData,
      },
    ]);
  } else if (!existingProduct) {
    // If no existing product, reset to default
    setSections([
      {
        selectedProduct: null,
        form: {
          wh_uom: "m",
          length_uom: "m",
          area_uom: "ft",
          place: "room",
          gst: 0,
          price: 0,
        },
      },
    ]);
  }
}, [existingProduct, productsList]);

  const fetchProducts = React.useCallback(async () => {
    if (!id || !authToken) return;
    setProductsLoading(true);
    setProductsLoadError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/products`, {
        params: { firstResults: 0, maxResults: 10000 },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setProductsList(res.data.products || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProductsList([]);
      setProductsLoadError("Could not load products. Check connection and try again.");
    } finally {
      setProductsLoading(false);
    }
  }, [id, authToken]);

  useEffect(() => {
    if (open && id && authToken) {
      fetchProducts();
    }
  }, [open, id, authToken, fetchProducts]);

  useEffect(() => {
    const fetchColors = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}account/${id}/colors`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        // Handle both response.data.colors and response.data.Colors
        const colorsList = res.data.colors || res.data.Colors || [];
        setColors(colorsList);
        console.log("Colors fetched:", colorsList);
      } catch (err) {
        console.error("Error fetching colors:", err);
        setColors([]); // Set empty array on error
      }
    };
    if (id && authToken) {
      fetchColors();
    }
  }, [id, authToken]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

const handleChange = (
  index: number,
  key: keyof ProductItem,
  value: any,
  isFinalPriceEdit?: boolean
) => {
  setSections((prev) => {
    const updated = [...prev];
    const isCustomProduct = updated[index].form.custom_name !== undefined;

    if (key === "price") {
      const priceValue = value === "" ? 0 : parseQuotationMoney(value);
      if (isCustomProduct && !isFinalPriceEdit) {
        updated[index].form.customProductBasePrice = priceValue;
        const nos = parseFloat(updated[index].form.nos?.toString() || "1");
        updated[index].form.price = priceValue * nos;
      } else {
        updated[index].form[key] = priceValue;
        if (isFinalPriceEdit) {
          setManualPriceEdited({ ...manualPriceEdited, [index]: true });
        }
      }
    } else if (key === "nos") {
      updated[index].form[key] = value;
      if (isCustomProduct && updated[index].form.customProductBasePrice !== undefined) {
        const basePrice = updated[index].form.customProductBasePrice;
        const nos = parseFloat(value?.toString() || "1");
        updated[index].form.price = basePrice * nos;
      }
    } else {
      updated[index].form[key] = value;
    }

    return updated;
  });
};


  const addSection = () => {
    setSections([
      ...sections,
      {
        selectedProduct: null,
        form: {
          width: 0,
          wh_uom: "m",
          length_uom: "m",
          nool_width: 0,
          height: 0,
          nool_height: 0,
          nos: 0,
          place: "room",
          area: 0,
          area_uom: "ft",
          area_per_unit: 0,
          gst: 0,
          price: 0,
          customProductBasePrice: undefined,
          custom_name: undefined,
        },
      },
    ]);
  };

  const duplicateSection = (index: number) => {
    const clone = JSON.parse(JSON.stringify(sections[index]));
    setSections([...sections.slice(0, index + 1), clone, ...sections.slice(index + 1)]);
  };

  const deleteSection = (index: number) => {
    if (sections.length === 1) return;
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const newSectionErrors: typeof sectionErrors = {};
    let hasErrors = false;

    // Validate each section
    sections.forEach((section, index) => {
      const errors: { [key: string]: string } = {};
      const { selectedProduct, form } = section;

      const isCustomLine = form.custom_name !== undefined;

      if (!selectedProduct && !isCustomLine) {
        errors.product = "Please select a product or add a custom line";
        hasErrors = true;
      }

      if (isCustomLine && !(String(form.custom_name || "").trim())) {
        errors.product = "Custom product name is required";
        hasErrors = true;
      }

      if (!form.nos || form.nos === 0) {
        errors.nos = "Quantity is required";
        hasErrors = true;
      }

      if (!form.description && !section.selectedProduct?.description) {
        errors.description = "Description is required";
        hasErrors = true;
      }

      if (isCustomLine && role !== "dealer") {
        const base = form.customProductBasePrice;
        if (base === undefined || base === null || Number(base) <= 0) {
          errors.price = "Base price (per unit) is required for custom products";
          hasErrors = true;
        }
      }

      if (Object.keys(errors).length > 0) {
        newSectionErrors[index] = errors;
      }
    });

    setSectionErrors(newSectionErrors);

    // If there are errors, scroll to first error
    if (hasErrors) {
      setTimeout(() => {
        const firstErrorIndex = Math.min(...Object.keys(newSectionErrors).map(Number));
        const errorSection = scrollContainerRef.current?.querySelector(`[data-section="${firstErrorIndex}"]`);
        if (errorSection) {
          errorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }

    const products: ProductItem[] = sections.map((s) => {
      const { selectedProduct, form } = s;
      const isCustomLine = form.custom_name !== undefined;
      const name = (isCustomLine ? String(form.custom_name || "").trim() : "") || selectedProduct?.name || "";
      return {
        product_id: isCustomLine ? "custom" : String(selectedProduct?.id || ""),
        name,
        description: (form.description || selectedProduct?.description || "").trim(),
        article_no: isCustomLine
          ? String((form as any).article_no || "").trim()
          : selectedProduct?.articleNo || "",
        hsn_code: isCustomLine
          ? String((form as any).hsn_code || "").trim()
          : selectedProduct?.hsnCode || "",
        unit: isCustomLine ? String((form as any).unit || "").trim() : selectedProduct?.unit || "",
        moq: form.moq ?? selectedProduct?.moq ?? 1,
        nos: form.nos || 1,
        price: form.price ?? selectedProduct?.price ?? 0,
        gst: form.gst ?? selectedProduct?.gst ?? 0,
        area: Number((form as any).area) || 0,
        area_uom: String((form as any).area_uom || "ft"),
        area_per_unit: Number((form as any).area_per_unit) || 0,
        width: Number((form as any).width) || 0,
        wh_uom: String((form as any).wh_uom || ""),
        nool_width: Number((form as any).nool_width) || 0,
        height: Number((form as any).height) || 0,
        nool_height: Number((form as any).nool_height) || 0,
        length: Number((form as any).length) || 0,
        length_uom: String((form as any).length_uom || ""),
        formulaType: String((form as any).formulaType || ""),
        place: String((form as any).place || ""),
        specifications: {},
      };
    });
    onAdd(products);
    // Reset sections after submitting
    setSections([
      {
        selectedProduct: null,
        form: {
          width: 0,
          wh_uom: "m",
          nool_width: 0,
          height: 0,
          nool_height: 0,
          length: 0,
          length_uom: "m",
          nos: 0,
          place: "room",
          area: 0,
          area_uom: "ft",
          area_per_unit: 0,
          gst: 0,
          price: 0,
          customProductBasePrice: undefined,
          custom_name: undefined,
        },
      },
    ]);
    setSectionErrors({});
    onClose();
  };

  return (
    <>
      {/* Drawer Overlay */}
      {open && !otherModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[75]" onClick={onClose} />
      )}

      {/* Drawer Container */}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-[80] transform transition-all duration-300 w-full md:w-[min(65vw,56rem)] md:h-auto max-h-[100dvh] md:max-h-[95vh] rounded-t-2xl md:rounded-lg flex flex-col overflow-hidden shadow-2xl ${
          open ? "translate-y-0 md:scale-100 pointer-events-auto opacity-100 visible" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none invisible md:visible"
        }`}
      >
        {/* Header */}
        <div className="bg-white px-6 py-5 border-b-2 border-emerald-800/30 shrink-0 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="font-bold text-2xl text-black drop-shadow-lg">
            {existingProduct ? "✏️ Edit Product" : "🛍️ Add Products"}
          </h2>
          <button
            onClick={onClose}
            className="text-black hover:text-green-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6 p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pb-8 pointer-events-auto" ref={scrollContainerRef}>
          {sections.map((section, index) => (
            <div
              key={index}
              data-section={index}
      className="border-2 border-emerald-200/50 rounded-xl p-5 shadow-md bg-gradient-to-br from-white to-emerald-50 hover:shadow-lg transition-all min-h-[270px]"
            >
              {/* Error Alert for Section */}
              {sectionErrors[index] && (
                <div className="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded-xl flex gap-2 animate-shake">
                  <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div className="text-red-700 text-xs font-bold">
                    {Object.values(sectionErrors[index]).map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                </div>
              )}
              {/* HEADER ROW */}
              <div className="mb-5">
                {/* First Row: Label and Buttons */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-3">
                  <Label className="font-bold text-gray-800">{index + 1}. Product {sectionErrors[index]?.product && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].product}</span>}</Label>
                  <div className="flex gap-2 w-full md:w-auto flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addSection}
                      className="border-2 border-green-400 text-green-700 hover:bg-green-50 font-bold flex-1 md:flex-auto rounded-lg h-10"
                    >
                      ➕ Add
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => duplicateSection(index)}
                      className="bg-cyan-500 text-white hover:bg-cyan-600 font-bold flex-1 md:flex-auto rounded-lg h-10"
                    >
                      📋 Duplicate
                    </Button>
                    {sections.length > 1 && (
                      <Button 
                        size="sm" 
                        onClick={() => deleteSection(index)}
                        className="bg-red-500 text-white hover:bg-red-600 font-bold flex-1 md:flex-auto rounded-lg h-10"
                      >
                        🗑️ Delete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Second Row: Product picker (inline — avoids modal clipping) */}
                <div
                  data-product-picker={index}
                  className={`${sectionErrors[index]?.product ? "bg-red-50 p-2 rounded-lg border border-red-200" : ""}`}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between border-2 border-emerald-300 hover:border-emerald-500 h-12 rounded-lg font-semibold text-gray-700"
                    onClick={() => {
                      if (previewProduct || previewColor) return;
                      const next = openProductPicker === index ? null : index;
                      setOpenProductPicker(next);
                      if (next === index) {
                        fetchProducts();
                        requestAnimationFrame(() => {
                          scrollContainerRef.current
                            ?.querySelector(`[data-product-picker="${index}"]`)
                            ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                        });
                      }
                    }}
                  >
                    <span className="truncate text-left flex-1 mr-2">
                      {section.selectedProduct?.name ||
                        (section.form.custom_name !== undefined
                          ? section.form.custom_name?.trim() || "Custom product"
                          : "Select Product")}
                    </span>
                    <span className="text-emerald-600 shrink-0">
                      {openProductPicker === index ? "▲" : "▼"}
                    </span>
                  </Button>

                  {openProductPicker === index && (
                    <div className="mt-2 w-full rounded-xl border-2 border-emerald-200 bg-white shadow-lg flex flex-col overflow-hidden">
                      <div className="p-3 sm:p-4 border-b-2 border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 shrink-0">
                        <Input
                          placeholder="🔍 Search product..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="border-2 border-emerald-300 rounded-lg h-10 sm:h-11 font-medium focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="max-h-52 sm:max-h-60 overflow-y-auto overscroll-contain p-2 sm:p-3 space-y-1">
                        {productsLoading && (
                          <p className="text-sm text-gray-500 text-center py-6">Loading products…</p>
                        )}
                        {!productsLoading && productsLoadError && (
                          <p className="text-sm text-red-600 text-center py-4 px-2">{productsLoadError}</p>
                        )}
                        {!productsLoading &&
                          !productsLoadError &&
                          productsList
                            .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
                            .map((p) => (
                              <div
                                key={p.id}
                                onClick={() => {
                                  const updated = [...sections];
                                  updated[index].selectedProduct = p;
                                  updated[index].form.gst = p.gst;
                                  updated[index].form.price = p.price;
                                  updated[index].form.moq = p.moq ?? 1;
                                  updated[index].form.description = p.description || "";
                                  updated[index].form.custom_name = undefined;
                                  setSections(updated);
                                  setSearch("");
                                  setOpenProductPicker(null);
                                }}
                                className={cn(
                                  "p-3 rounded-lg cursor-pointer hover:bg-emerald-100 transition-all border-l-4 border-transparent flex items-center gap-3",
                                  section.selectedProduct?.id === p.id &&
                                    "bg-emerald-50 border-l-4 border-emerald-600 shadow-sm font-bold",
                                )}
                              >
                                {p.productImage ? (
                                  <img
                                    src={p.productImage}
                                    alt={p.name}
                                    className="w-10 h-10 rounded border border-emerald-300 object-cover flex-shrink-0 cursor-pointer hover:shadow-lg hover:scale-110 transition-all"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewProduct(p);
                                    }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded border border-emerald-300 bg-gray-200 flex items-center justify-center flex-shrink-0">
                                    <span className="text-gray-500 text-xs">📦</span>
                                  </div>
                                )}
                                <div className="font-semibold text-gray-900 text-sm sm:text-base min-w-0 flex-1 break-words leading-snug">
                                  {p.name}
                                </div>
                              </div>
                            ))}
                        {!productsLoading &&
                          !productsLoadError &&
                          productsList.filter((p) =>
                            p.name.toLowerCase().includes(search.toLowerCase()),
                          ).length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-6">
                              {productsList.length === 0
                                ? "No products in catalog. Add products from the Products menu first."
                                : "No products match your search."}
                            </p>
                          )}
                      </div>
                      <div className="border-t-2 border-emerald-100 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-cyan-50 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...sections];
                            updated[index].selectedProduct = null;
                            updated[index].form.custom_name = "";
                            updated[index].form.nos = updated[index].form.nos || 1;
                            updated[index].form.moq = updated[index].form.moq ?? 1;
                            setSections(updated);
                            setSearch("");
                            setOpenProductPicker(null);
                          }}
                          className={cn(
                            "w-full p-3 rounded-lg cursor-pointer font-bold transition-all border-2 text-center",
                            !section.selectedProduct && section.form.custom_name !== undefined
                              ? "bg-blue-100 border-blue-600 text-blue-700 shadow-md"
                              : "border-blue-400 text-blue-600 hover:bg-blue-50",
                          )}
                        >
                          + Add Custom Product
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {section.form.custom_name !== undefined && (
                <>
                 <div className="mb-4 p-3 rounded-lg border-2 border-blue-300 bg-blue-50">
                  <Label className="font-bold text-gray-800 mb-2 block">Custom Product Name</Label>
                  <Input
                    placeholder="Enter custom product name"
                    value={section.form.custom_name || ""}
                    onChange={(e) => handleChange(index, "custom_name", e.target.value)}
                    className="border-2 border-blue-300 rounded-lg font-medium"
                  />
                </div>
                {/* Base Price Field for Custom Products */}
                {role != "dealer" && (
                  <div className="mb-4 p-3 rounded-lg border-2 border-green-300 bg-green-50">
                    <Label className="font-bold text-gray-800 mb-2 block">💵 Base Price * (per unit)</Label>
                    <Input
                      type="text"
                      placeholder="Enter base price per unit"
                      value={
                        section.form.customProductBasePrice !== undefined
                          ? formatQuotationMoney(section.form.customProductBasePrice)
                          : ""
                      }
                      onChange={(e) =>
                        handleChange(index, "price", e.target.value === "" ? 0 : parseQuotationMoney(e.target.value), false)
                      }
                      className="border-2 border-green-300 rounded-lg font-medium"
                    />
                    {sectionErrors[index]?.price && (
                      <p className="text-xs text-red-600 font-semibold mt-1">{sectionErrors[index].price}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">Final line price = Base Price × Quantity</p>
                  </div>
                )}
                {role !== "dealer" && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-lg border-2 border-gray-200 bg-white">
                      <Label className="font-bold text-gray-700 mb-1 block text-xs">Article No</Label>
                      <Input
                        placeholder="Optional"
                        value={String((section.form as any).article_no || "")}
                        onChange={(e) => handleChange(index, "article_no" as keyof ProductItem, e.target.value)}
                        className="border-2 border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div className="p-3 rounded-lg border-2 border-gray-200 bg-white">
                      <Label className="font-bold text-gray-700 mb-1 block text-xs">HSN Code</Label>
                      <Input
                        placeholder="Optional"
                        value={String((section.form as any).hsn_code || "")}
                        onChange={(e) => handleChange(index, "hsn_code" as keyof ProductItem, e.target.value)}
                        className="border-2 border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div className="p-3 rounded-lg border-2 border-gray-200 bg-white">
                      <Label className="font-bold text-gray-700 mb-1 block text-xs">Unit</Label>
                      <Input
                        placeholder="e.g. pcs, sq ft"
                        value={String((section.form as any).unit || "")}
                        onChange={(e) => handleChange(index, "unit" as keyof ProductItem, e.target.value)}
                        className="border-2 border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
                </>
              )}

              {/* PRODUCT SPECIFICATIONS - removed */}

              {/* Place - removed */}

              {/* Width/Height/Length - removed */}

              {/* Quantity */}
              {section && (section.selectedProduct || section.form.custom_name !== undefined) && (
                <>
                  {/* Description */}
                  <div className="mt-4 bg-white p-3 rounded-lg border-2 border-purple-200">
                    <Label className="font-bold text-gray-700 mb-2 block">📝 Description {sectionErrors[index]?.description && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].description}</span>}</Label>
                    <textarea
                      rows={2}
                      value={section.form.description || ""}
                      onChange={(e) => handleChange(index, "description", e.target.value)}
                      placeholder="Enter product description"
                      className={`w-full border-2 rounded-lg font-medium px-3 py-2 text-sm focus:outline-none resize-none ${sectionErrors[index]?.description ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-purple-300 focus:border-purple-500'}`}
                    />
                  </div>
                  {/* Qty & MOQ */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.nos ? 'border-red-400 bg-red-50' : 'border-indigo-200'}`}>
                      <Label className="font-bold text-gray-700 mb-2 block">📦 Quantity {sectionErrors[index]?.nos && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].nos}</span>}</Label>
                      <Input
                        value={section.form.nos || ""}
                        type="number"
                        onChange={(e) => handleChange(index, "nos", parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="border-2 border-indigo-300 rounded-lg font-bold text-indigo-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div className="bg-white p-3 rounded-lg border-2 border-teal-200">
                      <Label className="font-bold text-gray-700 mb-2 block">🔢 MOQ</Label>
                      <Input
                        value={section.form.moq ?? 1}
                        type="number"
                        min={1}
                        onChange={(e) => handleChange(index, "moq", parseInt(e.target.value) || 1)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="border-2 border-teal-300 rounded-lg font-bold text-teal-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>
              </>)}

              {/* Price & GST */}
              {role !== "dealer" && section && (section.selectedProduct || section.form.custom_name !== undefined) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-white p-3 rounded-lg border-2 border-rose-200">
                    <Label className="font-bold text-gray-700 mb-2 block">💰 Price (₹)</Label>
                    <Input
                      type="text"
                      step="0.01"
                      value={section.form.price !== undefined && section.form.price !== "" && section.form.price !== 0 ? formatQuotationMoney(section.form.price) : ""}
                      onChange={(e) =>
                        handleChange(index, "price", e.target.value === "" ? 0 : parseQuotationMoney(e.target.value), true)
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      className="border-2 border-rose-300 rounded-lg font-bold text-rose-600"
                    />
                  </div>
                  <div className="bg-white p-3 rounded-lg border-2 border-amber-200">
                    <Label className="font-bold text-gray-700 mb-2 block">🔢 GST %</Label>
                    <Input
                      type="number"
                      value={section.form.gst !== undefined && section.form.gst !== null ? section.form.gst : ""}
                      onChange={(e) => handleChange(index, "gst", e.target.value === "" ? undefined : parseFloat(e.target.value))}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="border-2 border-amber-300 rounded-lg font-bold text-amber-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gradient-to-r from-gray-50 to-emerald-50 rounded-b-2xl md:rounded-b-lg flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => {
              setSections([{ selectedProduct: null, form: { gst: 0, price: 0, customProductBasePrice: undefined } }]);
              onClose();
            }}
            className="border-2 border-gray-400 hover:bg-gray-100 font-bold text-gray-700 h-11 rounded-lg"
          >
            ✕ Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold hover:shadow-lg hover:shadow-emerald-500/40 transition-all h-11 rounded-lg px-6"
          >
            ✓ Submit
          </Button>
        </div>
      </div>

      {/* Image Preview Drawer */}
      {previewColor && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4" onClick={() => setPreviewColor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewColor(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-pink-300 shadow-md flex items-center justify-center bg-gray-50">
              {previewColor?.colorImage ? (
                <img
                  src={previewColor.colorImage}
                  alt={previewColor.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">No image available</span>
                </div>
              )}
            </div>

            {/* Color Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewColor.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Color</p>
            </div>
          </div>
        </div>
      )}

      {/* Product Image Preview Modal */}
      {previewProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-4" onClick={() => setPreviewProduct(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewProduct(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-blue-300 shadow-md flex items-center justify-center bg-gray-50">
              {previewProduct?.productImage ? (
                <img
                  src={previewProduct.productImage}
                  alt={previewProduct.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">No image available</span>
                </div>
              )}
            </div>

            {/* Product Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewProduct.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Product</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
